import { ArrowUpRight } from "lucide-react";

interface SmartPrompt {
  title: string;
  hint: string;
  prompt: string;
}

interface InsightsSmartPromptsProps {
  genieName: string | null;
  dateRange: string;
  conversationCount: number;
  onAsk: (prompt: string) => void;
}

const MASCOT_SRC = "/genie-mascots/role/support.png";

const DATE_RANGE_LABEL: Record<string, string> = {
  "7d": "the last 7 days",
  "30d": "the last 30 days",
  "90d": "the last 90 days",
  all: "all time",
};

export const InsightsSmartPrompts = ({
  genieName,
  dateRange,
  conversationCount,
  onAsk,
}: InsightsSmartPromptsProps) => {
  const scope = genieName ? `for ${genieName}` : "across all my genies";
  const rangeLabel = DATE_RANGE_LABEL[dateRange] ?? "the last 30 days";
  const countPrefix = conversationCount > 0 ? `the ${conversationCount} ` : "";

  const prompts: SmartPrompt[] = [
    {
      title: "What's the story this period?",
      hint: "Weekly briefing",
      prompt: `Give me a briefing on conversations ${scope} over ${rangeLabel}. What changed since the prior period? Top topics, sentiment shifts, anything noteworthy. Keep it under 200 words.`,
    },
    {
      title: "Where is my genie struggling?",
      hint: "Hesitations & hand-offs",
      prompt: `Look at conversations ${scope} over ${rangeLabel}. Where did my genie hesitate, hedge, or hand off? Pull 3-5 concrete examples with quotes, and recommend what to add to the knowledge base or system prompt.`,
    },
    {
      title: "Show me hot leads I missed",
      hint: "Buying signals",
      prompt: `Scan conversations ${scope} from ${rangeLabel} for buying signals — pricing questions, timeline urgency, decision-maker mentions, comparisons with competitors. Rank the top 5 leads worth a personal follow-up and explain why.`,
    },
    {
      title: "Coach my genie",
      hint: "One change to ship",
      prompt: `Based on ${countPrefix}conversations ${scope} over ${rangeLabel}, give me ONE concrete change I should make to my genie this week — a prompt tweak, a knowledge base addition, or a goal field. Tell me exactly what to add and why.`,
    },
    {
      title: "What questions keep coming up?",
      hint: "Recurring asks",
      prompt: `Across ${countPrefix}conversations ${scope} from ${rangeLabel}, cluster the questions customers asked. Show me the top 5 recurring questions, how often each came up, and how my genie typically answered. Flag any where the answer was inconsistent.`,
    },
    {
      title: "Audit my worst calls",
      hint: "What went wrong",
      prompt: `Pull the lowest-sentiment or shortest calls ${scope} over ${rangeLabel}. For each one, tell me what specifically went wrong, what the customer wanted, and what my genie should have done instead. Be blunt.`,
    },
    {
      title: "What objections am I hearing?",
      hint: "Patterns to address",
      prompt: `Scan conversations ${scope} from ${rangeLabel} for objections — price pushback, feature gaps, trust concerns, comparisons. Rank them by frequency and tell me how my genie currently handles each one. Suggest sharper rebuttals where weak.`,
    },
    {
      title: "Where are competitors mentioned?",
      hint: "Win/loss intel",
      prompt: `Find every mention of a competitor or alternative tool across conversations ${scope} from ${rangeLabel}. For each, capture the competitor name, the context (comparison, switching from, evaluating), and what the customer said about us versus them.`,
    },
    {
      title: "Draft my follow-up emails",
      hint: "Ready to send",
      prompt: `Look at conversations ${scope} from ${rangeLabel} where a follow-up is owed — leads with contact info, unanswered questions, calls that ended without a clear next step. Draft a short, specific follow-up email for the top 3 — pull real details from the call, no generic templates.`,
    },
    {
      title: "What should I add to the knowledge base?",
      hint: "Fill the gaps",
      prompt: `Across ${countPrefix}conversations ${scope} from ${rangeLabel}, find every moment where my genie said it didn't know, couldn't find, or had to check. Group them into 3-5 themes and tell me exactly what content I should add to the knowledge base to close each gap.`,
    },
    {
      title: "How am I doing vs last period?",
      hint: "Period comparison",
      prompt: `Compare conversations ${scope} from ${rangeLabel} to the prior equivalent period. Tell me: volume change, sentiment shift, topic differences, escalation rate, lead capture rate. What's improving, what's getting worse, and why?`,
    },
    {
      title: "Which calls are my best examples?",
      hint: "Wins to replicate",
      prompt: `Pull the best-performing calls ${scope} from ${rangeLabel} — high sentiment, lead captured, goals met, customer happy. Tell me what my genie did well on each one so I can reinforce those patterns.`,
    },
  ];

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden mb-6">
      <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-3">
        <img src={MASCOT_SRC} alt="" className="h-6 w-6 object-contain flex-shrink-0" />
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900">Ask Your Genie</h3>
          <p className="text-xs text-gray-500">
            Pre-baked questions that turn this dataset into a decision
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 p-3">
        {prompts.map((p) => (
          <button
            key={p.title}
            type="button"
            onClick={() => onAsk(p.prompt)}
            className="group relative text-left rounded-lg border border-gray-200 hover:border-transparent hover:shadow-sm hover:shadow-genie-primary/15 transition-all px-3 py-2.5"
            title={p.hint}
          >
            <span
              className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
              style={{
                padding: "1px",
                background: "linear-gradient(135deg, #4E9CFF 0%, #FF4DB8 100%)",
                WebkitMask:
                  "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                WebkitMaskComposite: "xor",
                maskComposite: "exclude",
              }}
            />
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-xs font-semibold text-gray-900 leading-snug line-clamp-2 group-hover:bg-gradient-to-r group-hover:from-[#4E9CFF] group-hover:to-[#FF4DB8] group-hover:bg-clip-text group-hover:text-transparent transition-colors">
                  {p.title}
                </div>
                <div className="text-[10px] text-gray-400 mt-0.5 truncate">
                  {p.hint}
                </div>
              </div>
              <ArrowUpRight className="w-3 h-3 text-gray-300 group-hover:text-[#FF4DB8] flex-shrink-0 mt-0.5" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};
